import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowRight, Money, Bank, Wallet } from "@phosphor-icons/react";

/**
 * Drilldown modal shown when the user clicks the amount inside the "Received"
 * HeroCard. Lists every payment received in the selected range, grouped and
 * sorted newest first. Each row links back to the parent order.
 */
export default function ReceivedPaymentsDialog({ open, range, onClose, onOpenOrder }) {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      setBusy(true);
      try {
        const { data } = await api.get(`/analytics/received-payments?range=${range}`);
        if (!cancel) setData(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        if (!cancel) setBusy(false);
      }
    })();
    return () => { cancel = true; };
  }, [open, range]);

  const openOrder = (id) => {
    onClose?.();
    setTimeout(() => onOpenOrder?.(id), 60);
  };

  const modeIcon = (mode) => {
    const m = String(mode || "").toLowerCase();
    if (m.includes("cash")) return <Money size={12} />;
    if (m.includes("upi") || m.includes("online")) return <Wallet size={12} />;
    return <Bank size={12} />;
  };

  const fmtDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-3xl bg-slate-900 border-slate-700 text-white rounded-sm max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="received-payments-dialog"
      >
        <DialogHeader>
          <div className="text-[10px] font-mono-num tracking-widest text-slate-400">// RECEIVED PAYMENTS</div>
          <DialogTitle className="font-heading text-2xl">{data?.label || range}</DialogTitle>
          <div className="flex items-baseline gap-4 flex-wrap mt-1">
            <div>
              <span className="text-[10px] font-mono-num tracking-widest text-slate-500">TOTAL RECEIVED</span>
              <div className="font-heading text-2xl font-black font-mono-num text-emerald-400">
                ₹{Number(data?.total ?? 0).toLocaleString("en-IN")}
              </div>
            </div>
            <div>
              <span className="text-[10px] font-mono-num tracking-widest text-slate-500">PAYMENTS</span>
              <div className="font-heading text-2xl font-black font-mono-num text-cyan-300">
                {data?.count ?? 0}
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 -mr-1">
          {busy && <div className="text-slate-500 text-sm py-10 text-center">Loading…</div>}
          {!busy && data && data.payments.length === 0 && (
            <div className="text-slate-500 text-sm py-10 text-center flex flex-col items-center gap-2">
              <Money size={26} className="text-slate-600" />
              No payments received in this range yet.
            </div>
          )}
          {!busy && data && data.payments.length > 0 && (
            <div className="space-y-2">
              {data.payments.map((p) => (
                <button
                  key={`${p.order_id}-${p.payment_id}`}
                  onClick={() => openOrder(p.order_id)}
                  className="w-full text-left surface-2 hover:bg-slate-800 transition-colors rounded-sm p-3 flex items-center gap-3 border border-slate-800 hover:border-slate-700"
                  data-testid={`received-payment-row-${p.payment_id || p.order_number}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono-num text-orange-400 font-bold text-sm">#{p.order_number}</span>
                      <span className="text-[10px] font-mono-num text-slate-500">{fmtDate(p.created_at)}</span>
                      <span className="text-[10px] font-mono-num tracking-widest px-1.5 py-0.5 rounded-sm bg-slate-800/60 border border-slate-700 text-slate-300 inline-flex items-center gap-1">
                        {modeIcon(p.mode)} {String(p.mode || "").toUpperCase()}
                      </span>
                    </div>
                    <div className="mt-1 text-slate-100 truncate">{p.customer_name || "—"}</div>
                    <div className="text-[10px] font-mono-num text-slate-500 mt-0.5">
                      {p.mobile}
                      {p.recorded_by_name && <span> · by {p.recorded_by_name}</span>}
                      {p.note && <span> · {p.note}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono-num text-emerald-400 font-bold text-lg">
                      ₹{Number(p.amount || 0).toLocaleString("en-IN")}
                    </div>
                  </div>
                  <ArrowRight size={14} className="text-slate-500 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
